/**
 * Pure helpers shared by the water-potential graph containers
 * (`AllFarmView/FieldDetailsWPGraph` and `WaterPotentialGraphContainer`).
 *
 * Both graphs (re)load their sensor lines only when `selectedField.id` changes
 * (mount + the field-change branch of `componentDidUpdate`). That is fine when
 * the user clicks a field, but NOT when the field is *auto-selected* on app
 * load / farming-unit switch: the field's sensors are (re)built with state
 * `init`, the initial `initGraph` can run before the field object is ready,
 * and the field-history refresh then replaces the field with a fresh `init`
 * sensor set at the *same id* — so the field-change branch never re-fires and
 * the sensor lines are never fetched. Symptom: the graph shows the sensor
 * chips but no curves until the user explicitly loads a field ("first field
 * load per farming unit").
 *
 * `wpSensorsNeedInitialLoad` detects that stuck state so a container can re-run
 * `initGraph` once, without an explicit re-selection. Callers must pass
 * `selectedSensors` so a successful first load is not wiped when history
 * refresh replaces `selectedField.sensors` with fresh `init` copies (that
 * mismatch caused a ~1s clear+reload flicker on farming-unit switch).
 */

// Mirror GeneralClasses.SENSOR_STATES. Kept as literals so these helpers stay
// pure/self-contained (no model imports) for node:test.
const SENSOR_STATE_INIT = 'init';
const SENSOR_STATE_LOADING = 'loading';

// Sensor types a WP graph draws. FieldDetailsWPGraph loads only 'wp'; the
// field-view WaterPotentialGraphContainer also loads 'piezometer'. Each
// container passes its own set via options.sensorTypes.
const DEFAULT_WP_SENSOR_TYPES = ['wp'];

/**
 * Ids of the field's loadable WP sensors. Filters by the `type` string (not
 * `instanceof`) so it works on the plain objects Redux rebuilds on field
 * change.
 *
 * @param {{sensors?: Object}|null|undefined} field
 * @param {string[]} [sensorTypes] - types this graph loads (default ['wp'])
 * @returns {string[]}
 */
const getLoadableWpSensorIds = (
  field,
  sensorTypes = DEFAULT_WP_SENSOR_TYPES,
) => {
  if (!field || !field.sensors) {
    return [];
  }
  return Object.keys(field.sensors).filter(
    (id) => field.sensors[id] && sensorTypes.includes(field.sensors[id].type),
  );
};

/**
 * True when any sensor in `selectedSensors` already has plotted samples.
 * Used to avoid a second full `initGraph` after field-history refresh:
 * `refreshSelectedEntities` swaps `selectedField` for the distributions-tree
 * copy whose sensors are still `init`/empty, even though the graph already
 * loaded into `selected_entities.sensors`. Without this guard the self-heal
 * clears the chart and refetches (~1s flicker on farming-unit switch).
 *
 * @param {Object|null|undefined} selectedSensors
 * @returns {boolean}
 */
const selectedSensorsHaveLoadedData = (selectedSensors) => {
  if (!selectedSensors) {
    return false;
  }
  return Object.keys(selectedSensors).some((id) => {
    const sensor = selectedSensors[id];
    return Boolean(sensor && sensor.data && sensor.data.length > 0);
  });
};

/**
 * True when the selected field has loadable WP sensors that were never loaded
 * — all still untouched (state `init`) with no samples — and nothing is
 * currently fetching. Guards keep it from looping or clobbering the user's
 * selection:
 *   - `loadingChipIds` empty (containers that track in-flight chips locally):
 *     an in-flight load blocks a re-fire.
 *   - no sensor in the `loading` state: the other in-flight signal, for
 *     containers that track loading on the sensor object.
 *   - no sensor has data: a deselected sensor keeps its data, so a manual
 *     "deselect all" does not retrigger a reload.
 *   - at least one sensor still `init`: a sensor whose fetch returned empty is
 *     left `complete` (not `init`), so genuinely-empty sensors aren't refetched.
 *   - `selectedSensors` has no loaded samples: after a successful first load,
 *     field-history refresh replaces `selectedField.sensors` with fresh `init`
 *     copies while Redux `selectedSensors` still holds the plotted data — do
 *     not treat that as stuck (would clear+reload the chart).
 *
 * @param {{sensors?: Object}|null|undefined} field - selectedField from Redux
 * @param {Object} [options]
 * @param {string[]} [options.loadingChipIds] - chips with a fetch in flight
 * @param {string[]} [options.sensorTypes] - types this graph loads
 * @param {Object} [options.selectedSensors] - Redux selected_entities.sensors
 * @returns {boolean}
 */
const wpSensorsNeedInitialLoad = (field, options = {}) => {
  const {
    loadingChipIds = [],
    sensorTypes = DEFAULT_WP_SENSOR_TYPES,
    selectedSensors,
  } = options;
  if (!field || !field.sensors) {
    return false;
  }
  if (loadingChipIds.length > 0) {
    return false;
  }
  // Graph already has curves from a prior successful load for this field —
  // history refresh must not re-trigger initGraph (flicker).
  if (selectedSensorsHaveLoadedData(selectedSensors)) {
    return false;
  }
  const ids = getLoadableWpSensorIds(field, sensorTypes);
  if (ids.length === 0) {
    return false;
  }
  const noneLoaded = ids.every(
    (id) => !(field.sensors[id].data && field.sensors[id].data.length > 0),
  );
  const anyUntouched = ids.some(
    (id) => field.sensors[id].state === SENSOR_STATE_INIT,
  );
  const noneInFlight = ids.every(
    (id) => field.sensors[id].state !== SENSOR_STATE_LOADING,
  );
  return noneLoaded && anyUntouched && noneInFlight;
};

module.exports = {
  SENSOR_STATE_INIT,
  SENSOR_STATE_LOADING,
  DEFAULT_WP_SENSOR_TYPES,
  getLoadableWpSensorIds,
  selectedSensorsHaveLoadedData,
  wpSensorsNeedInitialLoad,
};
